import { computed, EffectRef, ResourceRef, signal, Signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';

import { delay, of, tap } from 'rxjs';
import { DeepSignal, Defined } from '@pt/models';

/**
 * Creates a deep signal
 * @param initialValue - an initial state value
 */
export function deepSignal<T extends object>(initialValue: T): DeepSignal<T> {
  const deepSignal: any = signal<T>(initialValue);

  Object.keys(initialValue).forEach(key => {
    deepSignal[key] = computed(() => deepSignal()[key]);
  });

  return deepSignal;
}

/**
 * Calls the effect function every time the signals in the computed function change,
 * except the first initial call
 * @param computedFn - returns event data from signals
 * @param effectFn - the effect function
 */
export function eventEffect<T>(computedFn: () => T, effectFn: (eventData: T) => void): EffectRef {
  let isFirstTime = true;

  const resource = rxResource({
    request: () => {
      const computedData = computedFn();
      const eventData = isFirstTime ? undefined : computedData;
      isFirstTime = false;
      return eventData;
    },
    loader: ({ request }) => of(true)
      .pipe(delay(1), tap(() => effectFn(request))),
  });

  return { destroy: () => resource.destroy() };
}

/**
 * Calculates the value from the resource value and fixes the issue of resetting
 * to the default value every time a resource load is performed
 * @param computedFn - returns a computed value from the resource value
 * @param resourceRef - the resource reference
 * @param defaultValue - the default value
 */
export function computedWithFallback<T, D>(
  computedFn: (value: Defined<T>) => D,
  resourceRef: ResourceRef<T>,
  defaultValue: T,
): Signal<D> {
  let prevValue = defaultValue;
  return computed(() => {
    const value = resourceRef.value();
    const newValue = typeof value === 'undefined' ? prevValue : value;
    prevValue = newValue;
    return computedFn(newValue as Defined<T>);
  });
}
